import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import LinearGradient from 'react-native-linear-gradient';
import Animated, {FadeInUp, FadeOut, Layout} from 'react-native-reanimated';
import {useNavigation} from '@react-navigation/native';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius} from '~constants';
import {SCREENS} from '~constants';
import {NavigationProps} from '~types/navigation/navigation.type';
import {useGetActivePlan} from '~hooks/subscription/useGetActivePlan';
import ModernCloseIcon from '~assets/icons/ModernCloseIcon';

const STORAGE_KEY = 'subscription_reminder_dismiss_count';
const MAX_DISMISSALS = 10;

const SubscriptionReminderTile = () => {
  const {colors, isDarkMode} = useTheme();
  const navigation = useNavigation<NavigationProps>();
  const {data: activePlan, isLoading} = useGetActivePlan();
  const [dismissCount, setDismissCount] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    const checkVisibility = async () => {
      try {
        const count = await AsyncStorage.getItem(STORAGE_KEY);
        const parsedCount = count ? parseInt(count, 10) : 0;
        setDismissCount(parsedCount);
        
        // Only show if:
        // 1. Not loading subscription data
        // 2. No active plan found
        // 3. Dismissed less than 10 times
        if (!isLoading && !activePlan /* && parsedCount < MAX_DISMISSALS */) {
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Error checking subscription reminder visibility:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    checkVisibility();
  }, [activePlan, isLoading]);

  const handleDismiss = async () => {
    try {
      const newCount = dismissCount + 1;
      setDismissCount(newCount);
      await AsyncStorage.setItem(STORAGE_KEY, newCount.toString());
      setIsVisible(false);
    } catch (error) {
      console.error('Error dismissing subscription reminder:', error);
    }
  };

  const handlePress = () => {
    navigation.navigate(SCREENS.SUBSCRIPTIONS);
  };

  if (!isInitialized || !isVisible) return null;

  return (
    <Animated.View 
      entering={FadeInUp ? FadeInUp.duration(600) : undefined} 
      exiting={FadeOut ? FadeOut.duration(400) : undefined}
      layout={Layout ? Layout.springify() : undefined}
      style={styles.wrapper}
    >
      <TouchableOpacity 
        activeOpacity={0.9} 
        onPress={handlePress}
        style={styles.touchable}
      >
        <LinearGradient
          colors={isDarkMode ? ['#FF950020', '#FF950010'] : ['#FF950015', '#FF950005']}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 1}}
          style={[styles.container, {borderColor: colors.primary + '40'}]}
        >
          <View style={styles.content}>
            <View style={styles.iconContainer}>
              <Text style={styles.emoji}>👑</Text>
            </View>
            
            <View style={styles.textContainer}>
              <Text style={[styles.title, {color: colors.text}]}>
                Unlock Premium Experience
              </Text>
              <Text style={[styles.subtitle, {color: colors.textSecondary}]}>
                Get access to unlimited dinners, cultural events and parties
              </Text>
            </View>

            <View style={[styles.button, {backgroundColor: colors.primary}]}>
              <Text style={styles.buttonText}>Upgrade</Text>
            </View>
          </View>
        </LinearGradient>
      </TouchableOpacity>

      <TouchableOpacity 
        style={styles.closeButton} 
        onPress={handleDismiss}
        hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
      >
        <ModernCloseIcon 
          size={18} 
          color={colors.textSecondary} 
          variant="minimal" 
        />
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
    position: 'relative',
    width: '95%',
    alignSelf: 'center'
  },
  touchable: {
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
      },
    }),
  },
  container: {
    padding: spacing.md,
    borderRadius: borderRadius.xl,
    borderWidth: 1,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FF950020',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  emoji: {
    fontSize: 20,
  },
  textContainer: {
    flex: 1,
    marginRight: spacing.sm,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
  },
  button: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: borderRadius.full,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 12,
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
  },
});

export default SubscriptionReminderTile;
